import { appLanguageSchema, TAppLanguage } from '../schemas/settings';

export interface AppLanguage {
  id: TAppLanguage;
  /**
   * The language's own name. Never translated, so people can find their
   * language whatever the interface currently shows.
   */
  nativeName: string;
}

const NATIVE_NAMES: Record<TAppLanguage, string> = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  hu: 'Magyar',
  it: 'Italiano',
  pl: 'Polski',
  // The translations are Brazilian Portuguese (see translate.py).
  pt: 'Português (Brasil)',
  tr: 'Türkçe',
};

/** Selectable interface languages, alphabetically by their own names. */
export const APP_LANGUAGES: readonly AppLanguage[] = appLanguageSchema.options
  .map((id) => ({ id, nativeName: NATIVE_NAMES[id] }))
  .sort((a, b) => a.nativeName.localeCompare(b.nativeName, 'en'));

/** The language's own name, e.g. `Deutsch` for `de`. */
export function nativeLanguageName(id: TAppLanguage): string {
  return NATIVE_NAMES[id];
}

export function isAppLanguage(value: unknown): value is TAppLanguage {
  return appLanguageSchema.safeParse(value).success;
}

/**
 * The first of the browser's preferred languages that the app supports, by
 * base language (`de-AT` → `de`); `null` when none is supported. Unlike
 * ngx-translate's `getBrowserLang()`, later preferences count too, so
 * `['nl-NL', 'de-DE']` resolves to `de` instead of falling back to English.
 */
export function resolveBrowserLanguage(preferred: readonly string[]): TAppLanguage | null {
  for (const tag of preferred) {
    const base = tag.split(/[-_]/)[0]?.toLowerCase();
    if (isAppLanguage(base)) return base;
  }
  return null;
}
